// The public demo (`https://demo.{domain}`, spec/24, ADR-0039): what `vite build --mode demo` makes.
//
// The organisation console's own routes and screens, with three differences (spec/24 §4.2):
//
//   createApi   the API client answers in the browser — the simulated venue and the console's fixtures — except
//               for sign-in, the demo window, `/v1/meta` and a new social account's profile, which go to the real api (`backend.ts`)
//   Frame       the real sign-in gates it; signed in, the window starts, the bar counts it down, and at the end
//               the console gives way to "Your 15-minute demo has ended" (`DemoFrame.tsx`)
//   realtime    no socket: the venue is in the page, and a change in the kiosk tab invalidates the screens
//
// Nothing outside `src/demo/` imports this directory, so the organisation and HQ builds never reach it
// (`src/__tests__/builds.test.ts`). Its copy is the console's namespace and the demo's own.

import { createApiClient } from '@clavian/api-client';
import { consoleResources } from '@clavian/i18n/console';
import { demoResources } from '@clavian/i18n/demo';

import { announceWrites } from '../api/writes';
import { createOrgRouteTree } from '../org/routes';
import type { ConsoleSurface } from '../surface';
import { createDemoFetch } from './backend';
import { DemoFrame } from './DemoFrame';
import { takeNextPage } from './next-page';
import { demoRuntime } from './runtime';

/** The demo build's name, on its document — and the marker the bundle test looks for. */
export const DEMO_SURFACE_NAME = 'clavian-demo-console';

export const surface: ConsoleSurface = {
  id: 'demo',
  name: DEMO_SURFACE_NAME,
  createRouteTree: createOrgRouteTree,
  resources: {
    en: { ...consoleResources.en, ...demoResources.en },
  },
  createApi: () =>
    createApiClient({
      // The demo origin's own `/v1`, forwarded to the api for the few real paths only (pages/proxy.ts).
      baseUrl: '',
      credentials: 'include',
      fetch: announceWrites(createDemoFetch({ runtime: demoRuntime, network: (input, init) => fetch(input, init) })),
    }),
  Frame: DemoFrame,
  // A new social account has just saved its profile: on to the kiosk or both-panes page it chose, else where it was going.
  afterProfileComplete: (landing) => takeNextPage() ?? landing,
  realtime: false,
};
